import { Capacitor } from '@capacitor/core'
import { getApiBaseUrl } from './apiConfig'
import { getCachedBlobUrl } from '@/lib/prefetch/offlineAttachmentCache'

/**
 * Convert a relative asset URL to an absolute URL on native platforms.
 *
 * On native platforms (iOS/Android), relative URLs like `/media/images/xxx.png`
 * resolve to `capacitor://localhost/media/images/xxx.png` which doesn't exist.
 * This function converts them to absolute URLs using the API base URL.
 *
 * On web, relative URLs work fine (same-origin via Caddy), so we return them unchanged.
 */
export function getAssetUrl(relativeUrl: string | undefined | null): string {
  if (!relativeUrl) return ''

  // Already absolute URL
  if (relativeUrl.startsWith('http://') || relativeUrl.startsWith('https://')) {
    return relativeUrl
  }

  // Data URLs (base64 images)
  if (relativeUrl.startsWith('data:')) {
    return relativeUrl
  }

  // Blob URLs
  if (relativeUrl.startsWith('blob:')) {
    return relativeUrl
  }

  // Capacitor file URLs (from convertFileSrc) — pass through as-is
  // iOS: capacitor://localhost/_capacitor_file_/...
  // Android: http://localhost/_capacitor_file_/...
  if (relativeUrl.includes('_capacitor_file_')) {
    return relativeUrl
  }

  // On native platforms, check offline cache first, then convert relative to absolute
  if (Capacitor.isNativePlatform()) {
    const cachedBlobUrl = getCachedBlobUrl(relativeUrl)
    if (cachedBlobUrl) return cachedBlobUrl

    const baseUrl = getApiBaseUrl()
    if (baseUrl) {
      const cleanBase = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl
      const cleanPath = relativeUrl.startsWith('/') ? relativeUrl : `/${relativeUrl}`
      return `${cleanBase}${cleanPath}`
    }
  }

  // Web: return as-is (relative URLs work via same-origin)
  return relativeUrl
}
